import { AboyeurPluginNamed } from '@ifood/aboyeur';
import * as Sentry from '@sentry/browser';

var SENTRY_BROWSER_REQUIRED_CONFIG_KEYS = ['dsn', 'release', 'environment'];
var SENTRY_BROWSER_MICROFRONT_REQUIRED_CONFIG_KEYS = ['dsn', 'release'];
/**
 * Sentry browser MetaData type guard
 */
function hasSentryBrowserMetadata(metadata) {
  if (!('sentryBrowser' in metadata)) {
    return false;
  }
  var sentryBrowser = metadata.sentryBrowser;
  if (typeof sentryBrowser !== 'object' || !sentryBrowser) {
    return false;
  }
  if ('mode' in sentryBrowser && sentryBrowser.mode === 'micro-frontend') {
    return SENTRY_BROWSER_MICROFRONT_REQUIRED_CONFIG_KEYS.every(function (key) {
      return Object.prototype.hasOwnProperty.call(sentryBrowser, key);
    });
  }
  return SENTRY_BROWSER_REQUIRED_CONFIG_KEYS.every(function (key) {
    return Object.prototype.hasOwnProperty.call(sentryBrowser, key);
  });
}
function isSentryBrowserError(error) {
  if (!error) {
    return false;
  }
  var isError = error instanceof Error;
  var isObject = typeof error === 'object';
  var isString = typeof error === 'string';
  return isError || isString || isObject;
}
var primitiveTypes = ['string', 'number', 'boolean'];
function isPrimitive(data) {
  return primitiveTypes.includes(typeof data);
}
function isTags(data) {
  if (typeof data !== 'object' || !data) {
    return false;
  }
  return Object.values(data).every(function (value) {
    if (typeof value == 'object' || typeof value == 'function') {
      return false;
    }
    return true;
  });
}
function sanitize(data) {
  if (!isPrimitive(data)) {
    return JSON.stringify(data);
  }
  return data;
}

function _defineProperties(target, props) {
  for (var i = 0; i < props.length; i++) {
    var descriptor = props[i];
    descriptor.enumerable = descriptor.enumerable || false;
    descriptor.configurable = true;
    if ("value" in descriptor) descriptor.writable = true;
    Object.defineProperty(target, _toPropertyKey(descriptor.key), descriptor);
  }
}
function _createClass(Constructor, protoProps, staticProps) {
  if (protoProps) _defineProperties(Constructor.prototype, protoProps);
  if (staticProps) _defineProperties(Constructor, staticProps);
  Object.defineProperty(Constructor, "prototype", {
    writable: false
  });
  return Constructor;
}
function _extends() {
  _extends = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends.apply(this, arguments);
}
function _inheritsLoose(subClass, superClass) {
  subClass.prototype = Object.create(superClass.prototype);
  subClass.prototype.constructor = subClass;
  _setPrototypeOf(subClass, superClass);
}
function _setPrototypeOf(o, p) {
  _setPrototypeOf = Object.setPrototypeOf ? Object.setPrototypeOf.bind() : function _setPrototypeOf(o, p) {
    o.__proto__ = p;
    return o;
  };
  return _setPrototypeOf(o, p);
}
function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;
  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }
  return target;
}
function _toPrimitive(input, hint) {
  if (typeof input !== "object" || input === null) return input;
  var prim = input[Symbol.toPrimitive];
  if (prim !== undefined) {
    var res = prim.call(input, hint || "default");
    if (typeof res !== "object") return res;
    throw new TypeError("@@toPrimitive must return a primitive value.");
  }
  return (hint === "string" ? String : Number)(input);
}
function _toPropertyKey(arg) {
  var key = _toPrimitive(arg, "string");
  return typeof key === "symbol" ? key : String(key);
}

var NAME = 'Sentry Browser Plugin';
var CATEGORY = 'Aboyeur';
var ERROR = {
  CONFIG: "Misconfigured " + NAME + " plugin metadata",
  MICRO_FRONTEND_NAME: "Oops tried setup " + NAME + " plugin but its missing the \"microFrontendName\" into " + NAME + " plugin config ",
  MICRO_FRONTEND_CONTAINER_NOT_SETUP: "Oops tried to get " + NAME + " plugin without initializing the plugin before in the CONTAINER app"
};

var getWindowInstance = function getWindowInstance() {
  var _window;
  var isBrowser = typeof window !== 'undefined';
  if (!isBrowser) return {};
  var aboyeur = (_window = window) == null ? void 0 : _window.__aboyeur__;
  var plugins = aboyeur == null ? void 0 : aboyeur.plugins;
  var plugin = plugins == null ? void 0 : plugins[NAME];
  var microFrontendInstances = plugin == null ? void 0 : plugin.microFrontendInstances;
  return {
    aboyeur: aboyeur,
    plugins: plugins,
    plugin: plugin,
    microFrontendInstances: microFrontendInstances
  };
};
var mergeMetadataToPlugin = function mergeMetadataToPlugin(partialMetadata) {
  var _extends2;
  var _getWindowInstance = getWindowInstance(),
    aboyeur = _getWindowInstance.aboyeur,
    plugins = _getWindowInstance.plugins,
    plugin = _getWindowInstance.plugin;
  var finalMetadata = _extends({}, plugin, partialMetadata);
  window.__aboyeur__ = _extends({}, aboyeur, {
    plugins: _extends({}, plugins, (_extends2 = {}, _extends2[NAME] = finalMetadata, _extends2))
  });
};
var mergeMicroFrontendInstance = function mergeMicroFrontendInstance(instances) {
  var _getWindowInstance2 = getWindowInstance(),
    microFrontendInstances = _getWindowInstance2.microFrontendInstances;
  mergeMetadataToPlugin({
    microFrontendInstances: _extends({}, microFrontendInstances, instances)
  });
};

// istanbul ignore next
var getInstanceByMode = function getInstanceByMode(mode) {
  if (mode === void 0) {
    mode = 'standalone';
  }
  if (mode !== 'micro-frontend') {
    return Sentry;
  }
  var _getWindowInstance = getWindowInstance(),
    plugin = _getWindowInstance.plugin;
  if (!plugin) {
    throw new Error(ERROR.MICRO_FRONTEND_CONTAINER_NOT_SETUP);
  }
  return plugin.instance;
};
var setGlobalInstance = function setGlobalInstance() {
  mergeMetadataToPlugin({
    instance: Sentry
  });
};
var setMicroFrontendInstance = function setMicroFrontendInstance(sentryBrowserConfig) {
  var _mergeMicroFrontendIn;
  var _getWindowInstance2 = getWindowInstance(),
    plugin = _getWindowInstance2.plugin;
  if (!plugin) {
    throw new Error(ERROR.MICRO_FRONTEND_CONTAINER_NOT_SETUP);
  }
  if (!sentryBrowserConfig.microFrontendName) {
    throw new Error(ERROR.MICRO_FRONTEND_NAME);
  }
  mergeMicroFrontendInstance((_mergeMicroFrontendIn = {}, _mergeMicroFrontendIn[sentryBrowserConfig.microFrontendName] = {
    DSN: sentryBrowserConfig.dsn,
    RELEASE: sentryBrowserConfig.release
  }, _mergeMicroFrontendIn));
};
// istanbul ignore next
var getMicroFrontendInstanceByName = function getMicroFrontendInstanceByName(name) {
  var _getWindowInstance3 = getWindowInstance(),
    microFrontendInstances = _getWindowInstance3.microFrontendInstances;
  if (!microFrontendInstances) {
    return;
  }
  var instance = microFrontendInstances[name];
  return instance;
};

var _excluded = ["user", "contexts", "tags", "mode"];
var SentryBrowserPlugin = /*#__PURE__*/function (_AboyeurPluginNamed) {
  _inheritsLoose(SentryBrowserPlugin, _AboyeurPluginNamed);
  function SentryBrowserPlugin() {
    return _AboyeurPluginNamed.apply(this, arguments) || this;
  }
  var _proto = SentryBrowserPlugin.prototype;
  _proto.initialize = function initialize() {
    var metadata = this.metadata;
    if (!hasSentryBrowserMetadata(metadata)) {
      throw new Error(ERROR.CONFIG);
    }
    var _metadata$sentryBrows = metadata.sentryBrowser,
      _metadata$sentryBrows2 = _metadata$sentryBrows.mode,
      mode = _metadata$sentryBrows2 === void 0 ? 'standalone' : _metadata$sentryBrows2,
      sentryInitConfig = _objectWithoutPropertiesLoose(_metadata$sentryBrows, _excluded);
    if (mode === 'micro-frontend') {
      setMicroFrontendInstance(metadata.sentryBrowser);
      return;
    }
    if (mode === 'micro-frontend-container') {
      setGlobalInstance();
    }
    this.instance.init(sentryInitConfig);
  };
  _proto.sendEvent = function sendEvent(options) {
    var label = options.label,
      entity = options.entity,
      feature = options.feature,
      metadata = options.metadata;
    var eventData = _extends({}, metadata.instance, metadata.event, {
      entity: entity,
      feature: feature,
      label: label
    });
    var _metadata$event = metadata.event,
      error = _metadata$event.error,
      _metadata$event$tags = _metadata$event.tags,
      tags = _metadata$event$tags === void 0 ? {} : _metadata$event$tags,
      _metadata$event$level = _metadata$event.level,
      level = _metadata$event$level === void 0 ? 'info' : _metadata$event$level;
    if (isSentryBrowserError(error) || level === 'error') {
      var _contexts2;
      this.instance.captureException(error, {
        contexts: (_contexts2 = {}, _contexts2[label] = eventData, _contexts2),
        tags: _extends({}, isTags(tags) && tags, {
          category: CATEGORY,
          entity: entity,
          feature: feature,
          label: label
        })
      });
    } else {
      this.instance.addBreadcrumb({
        message: label,
        category: CATEGORY,
        level: level,
        data: eventData
      });
    }
  };
  _proto.onMetadataChange = function onMetadataChange() {
    var _this = this;
    if (!hasSentryBrowserMetadata(this.metadata)) {
      return;
    }
    var sentryBrowser = this.metadata.sentryBrowser;
    if (sentryBrowser.user) {
      this.instance.setUser(sentryBrowser.user);
    }
    var contexts = sentryBrowser.contexts,
      tags = sentryBrowser.tags;
    if (contexts) {
      Object.entries(contexts).forEach(function (_ref) {
        var contextName = _ref[0],
          context = _ref[1];
        _this.instance.setContext(contextName, context);
      });
    }
    if (tags) {
      this.instance.setTags(tags);
    }
  };
  _createClass(SentryBrowserPlugin, [{
    key: "instance",
    get: function get() {
      var metadata = this.metadata;
      // istanbul ignore next
      if (!hasSentryBrowserMetadata(metadata)) {
        throw new Error(ERROR.CONFIG);
      }
      return getInstanceByMode(metadata.sentryBrowser.mode);
    }
  }]);
  return SentryBrowserPlugin;
}( /*#__PURE__*/AboyeurPluginNamed(NAME));

export { SentryBrowserPlugin, getMicroFrontendInstanceByName, hasSentryBrowserMetadata, isPrimitive, isSentryBrowserError, isTags, sanitize };
//# sourceMappingURL=aboyeur-plugin-sentry-browser.esm.js.map
